import { useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'

import axios from 'utils/axios/modules/pay'
import { getGmoToken } from 'utils/gmo'
import * as yup from 'yup'

async function requestPayment(id, setIsGettingPaymentAPI, setPayment, setGetPaymentErrors) {
  setIsGettingPaymentAPI(true)

  await axios
    .getPayment(id)
    .then((response) => {
      setPayment(response.data)
    })
    .catch((errorMessages) => {
      setGetPaymentErrors(errorMessages)
    })

  setIsGettingPaymentAPI(false)
}

async function requestCreditCard(id, setIsCreditCardRequestingAPI, setCreditCard, setCreditCardErrors) {
  setIsCreditCardRequestingAPI(true)

  await axios
    .getCreditCard(id)
    .then((response) => {
      setCreditCard(response.data.credit_card)
    })
    .catch((errorMessages) => {
      setCreditCardErrors(errorMessages)
    })

  setIsCreditCardRequestingAPI(false)
}

function requestUpdateCreditCard(id, data, setIsUpdateCCRequestingAPI, setUpdateCCErrors) {
  const expiration_year = data.expirationYear
  const expiration_month = data.expirationMonth
  const body = {
    cardno: data.creditCardNumber,
    expire: ('0000' + expiration_year.toString(10)).slice(-4) + ('00' + expiration_month.toString(10)).slice(-2),
    securitycode: data.securityCode,
    holdername: data.creditCardName,
  }

  // promise直列実行
  return new Promise((resolve, reject) => {
    setIsUpdateCCRequestingAPI(true)

    getGmoToken(body)
      .then((response) => {
        resolve(response)
      })
      .catch((errorMessages) => {
        reject(errorMessages)
      })
  })
    .then((gmoToken) => {
      return new Promise((resolve, reject) => {
        axios
          .getValidityCheck(id, gmoToken, body.holdername)
          .then((response) => {
            setIsUpdateCCRequestingAPI(false)
            window.location.href = response.data?.data?.redirect_url
          })
          .catch((errorMessages) => {
            reject(errorMessages)
          })
      })
    })
    .catch((errorMessages) => {
      setUpdateCCErrors(errorMessages)
      setIsUpdateCCRequestingAPI(false)

      return Promise.reject(errorMessages)
    })
}

function usePayHook() {
  // id関連
  const { id } = useParams()

  // creditCard関連(GET)
  const [creditCard, setCreditCard] = useState({})
  const [isCreditCardRequestingAPI, setIsCreditCardRequestingAPI] = useState(false)
  const [creditCardErrors, setCreditCardErrors] = useState([])
  const getCreditCard = useCallback(async () => {
    await requestCreditCard(id, setIsCreditCardRequestingAPI, setCreditCard, setCreditCardErrors)
  }, [id])

  const [isUpdateCCRequestingAPI, setIsUpdateCCRequestingAPI] = useState(false)
  const [updateCCErrors, setUpdateCCErrors] = useState([])

  const postCreditCard = useCallback(
    async (data) => {
      return await requestUpdateCreditCard(id, data, setIsUpdateCCRequestingAPI, setUpdateCCErrors)
    },
    [id]
  )

  // payment関連
  const [payment, setPayment] = useState({})
  const [isGettingPaymentAPI, setIsGettingPaymentAPI] = useState(false)
  const [getPaymentErrors, setGetPaymentErrors] = useState([])
  const getPayment = useCallback(async () => {
    await requestPayment(id, setIsGettingPaymentAPI, setPayment, setGetPaymentErrors)
  }, [id])

  const validationSchema = yup.object().shape({
    creditCardNumber: yup
      .string()
      .numberOnly('クレジットカード番号は、半角英数字で入力してください。全角文字は仕様できません。')
      .charLimit(
        10,
        16,
        'クレジットカード番号は、ブランドによって桁数が異なります。お手元のカードをご確認下さい。\nVisa、Mastercard、JCB：16桁\nアメリカン・エキスプレス：15桁\nDiners Club：14桁'
      )
      .required('クレジットカード番号は、入力必須です。')
      .specialCharacter('', 'クレジットカード番号は、半角英数字で入力してください。全角文字は仕様できません。'),
    creditCardName: yup
      .string()
      .acceptSpecialChar(
        /^[a-zA-Z0-9.,\-/ ]+$/,
        'クレジットカード名義には、半角英数字、半角スペースおよび、下記の半角特殊記号が使用できます。\n使用できる半角特殊記号： .（ピリオド）/ （スラッシュ） -（ハイフン）'
      )
      .maxChar(50, 'クレジットカード名義は、半角50文字以内で入力してください。')
      .required('クレジットカード名義は、入力必須です。')
      .specialCharacter(
        '',
        'クレジットカード名義には、半角英数字、半角スペースおよび、下記の半角特殊記号が使用できます。使用できる半角特殊記号： .（ピリオド）/ （スラッシュ） -（ハイフン）'
      ),
    expirationYear: yup
      .string()
      .numberOnly()
      .required('有効期限は入力必須です。'),
    expirationMonth: yup
      .string()
      .numberOnly()
      .required('有効期限は入力必須です。'),
    securityCode: yup
      .string()
      .numberOnly('セキュリティコードは、半角数字で入力してください。')
      .charLimit(3, 4, 'セキュリティコードは、3～4桁の半角数字で入力してください。')
      .required('セキュリティコードは入力必須です。')
      .specialCharacter('', 'セキュリティコードは、半角数字で入力してください。'),
  })

  return {
    // id関連
    contractId: id,

    // creditCard関連(GET)
    creditCard,
    isCreditCardRequestingAPI,
    creditCardErrors,
    setCreditCardErrors,
    getCreditCard,

    // creditCard関連(UPDATE)
    isUpdateCCRequestingAPI,
    updateCCErrors,
    setUpdateCCErrors,
    postCreditCard,

    // payment関連
    payment,
    isGettingPaymentAPI,
    getPaymentErrors,
    setGetPaymentErrors,
    getPayment,

    // validation
    validationSchema,
  }
}

export default usePayHook
